import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import { uploadVideoSubtitleAction, deleteVideoSubtitleAction, getVideoSubtitlesAction, patchVideoSubtitleAction } from 'src/reducers/course/action'
import { languages } from 'src/model/languages'

const getLangLabel = code => {
  const lang = languages.find(l => l.countryCode === code)
  return lang ? lang.name : code
}

const SubtitleManager = ({ shopClipId, videoSrc, onShopClipIdResolved }) => {
  const dispatch = useDispatch()
  const [subtitles, setSubtitles] = useState([])
  const [resolvedClipId, setResolvedClipId] = useState(shopClipId)

  const fetchSubtitles = (clipId, url) => {
    if (clipId || url) {
      dispatch(getVideoSubtitlesAction(clipId, url, (data) => {
        setSubtitles(Array.isArray(data) ? data : [])
      }))
    }
  }

  // Fetch subtitles from GET API on mount
  useEffect(() => {
    fetchSubtitles(resolvedClipId, videoSrc)
  }, [resolvedClipId, videoSrc])

  // Add new subtitle state
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [language, setLanguage] = useState('en')
  const [label, setLabel] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [uploading, setUploading] = useState(false)

  // Edit state
  const [editingLang, setEditingLang] = useState(null)
  const editFileRef = useRef(null)
  const [editFile, setEditFile] = useState(null)
  const [editExistingUrl, setEditExistingUrl] = useState(null)
  const [editLanguage, setEditLanguage] = useState('')
  const [editLabel, setEditLabel] = useState('')
  const [editIsDefault, setEditIsDefault] = useState(false)
  const [editOriginalDefault, setEditOriginalDefault] = useState(false)
  const [editSaving, setEditSaving] = useState(false)

  // Add form visibility
  const [showAddForm, setShowAddForm] = useState(false)

  // Delete state
  const [deletingLang, setDeletingLang] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleFileSelect = e => {
    const file = e.target.files[0]
    if (file) setSelectedFile(file)
    e.target.value = ''
  }

  const handleUpload = () => {
    if (!selectedFile || !language) return
    setUploading(true)
    dispatch(
      uploadVideoSubtitleAction(
        resolvedClipId,
        videoSrc,
        selectedFile,
        language,
        label || undefined,
        isDefault,
        (returnedShopClipId) => {
          setUploading(false)
          setSelectedFile(null)
          setLabel('')
          setIsDefault(false)
          setShowAddForm(false)
          if (returnedShopClipId) {
            setResolvedClipId(returnedShopClipId)
            if (onShopClipIdResolved) {
              onShopClipIdResolved(returnedShopClipId)
            }
          }
          fetchSubtitles(returnedShopClipId || resolvedClipId, videoSrc)
        },
      ),
    )
  }

  const startEdit = sub => {
    setEditingLang(sub.language)
    setEditFile(null)
    setEditExistingUrl(sub.url || null)
    setEditLanguage(sub.language)
    setEditLabel(sub.label || '')
    setEditIsDefault(sub.default || false)
    setEditOriginalDefault(sub.default || false)
  }

  const cancelEdit = () => {
    setEditingLang(null)
    setEditFile(null)
    setEditExistingUrl(null)
  }

  const handleEditSave = () => {
    const fileChanged = !!editFile
    const defaultChanged = editIsDefault !== editOriginalDefault

    if (!fileChanged && !defaultChanged) return

    setEditSaving(true)
    let pending = 0

    const onDone = () => {
      pending--
      if (pending <= 0) {
        setEditSaving(false)
        setEditingLang(null)
        setEditFile(null)
        setEditExistingUrl(null)
        fetchSubtitles(resolvedClipId, videoSrc)
      }
    }

    if (fileChanged) {
      pending++
      dispatch(
        uploadVideoSubtitleAction(
          resolvedClipId,
          videoSrc,
          editFile,
          editLanguage,
          editLabel || undefined,
          editIsDefault,
          (returnedShopClipId) => {
            if (returnedShopClipId && returnedShopClipId !== resolvedClipId) {
              setResolvedClipId(returnedShopClipId)
              if (onShopClipIdResolved) onShopClipIdResolved(returnedShopClipId)
            }
            onDone()
          },
        ),
      )
    }

    if (defaultChanged && !fileChanged) {
      pending++
      dispatch(
        patchVideoSubtitleAction(
          resolvedClipId,
          videoSrc,
          editLanguage,
          [{ op: 'replace', path: '/default', value: editIsDefault }],
          onDone,
        ),
      )
    }
  }

  const handleDelete = lang => {
    setDeleteLoading(true)
    dispatch(
      deleteVideoSubtitleAction(
        resolvedClipId,
        videoSrc,
        lang,
        () => {
          setDeleteLoading(false)
          setDeletingLang(null)
          fetchSubtitles(resolvedClipId, videoSrc)
        },
      ),
    )
  }

  const isExternalUrl = (url) => {
    if (!url) return false
    const externalHosts = ['vimeo.com', 'youtube.com', 'youtu.be', 'dailymotion.com', 'wistia.com', 'loom.com']
    return externalHosts.some(host => url.includes(host))
  }

  if (!resolvedClipId && isExternalUrl(videoSrc)) {
    return (
      <div style={{ padding: '24px', minWidth: '400px', textAlign: 'center' }}>
        <Icon icon="mdi:video-off-outline" width={48} style={{ color: '#919EAB', marginBottom: '12px' }} />
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '8px' }}>
          Video upload required
        </Typography>
        <Typography variant="body2" style={{ color: '#637381' }}>
          Subtitles are only visible on Preview and Learner side.
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ padding: '16px', minWidth: '400px', maxHeight: '60vh', overflowY: 'auto' }}>
      {/* Existing subtitles */}
      {subtitles.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <Typography
            variant="subtitle2"
            style={{ marginBottom: '8px', fontWeight: 600 }}
          >
            Subtitles
          </Typography>
          {subtitles.map(sub => (
            <div key={sub.language} style={{ marginBottom: '6px' }}>
              {/* Delete confirmation */}
              {deletingLang === sub.language ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: '#FFF2F0',
                    border: '1px solid #FFCCC7',
                  }}
                >
                  <Typography variant="body2" style={{ color: '#CF1322' }}>
                    Delete "{getLangLabel(sub.language)}"?
                  </Typography>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <CoreButton
                      size="small"
                      variant="contained"
                      style={{ background: '#FF4D4F', color: '#fff', minWidth: 'auto', padding: '2px 12px', fontSize: '12px' }}
                      onClick={() => handleDelete(sub.language)}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
                    </CoreButton>
                    <CoreButton
                      size="small"
                      variant="outlined"
                      style={{ minWidth: 'auto', padding: '2px 12px', fontSize: '12px' }}
                      onClick={() => setDeletingLang(null)}
                      disabled={deleteLoading}
                    >
                      Cancel
                    </CoreButton>
                  </div>
                </div>
              ) : editingLang === sub.language ? (
                /* Edit form */
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#F0F7FF',
                    border: '1px solid #D6E4FF',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Edit file picker */}
                    <div>
                      <input
                        type="file"
                        ref={editFileRef}
                        accept=".srt,.vtt"
                        onChange={e => {
                          const f = e.target.files[0]
                          if (f) {
                            setEditFile(f)
                            setEditExistingUrl(null)
                          }
                          e.target.value = ''
                        }}
                        style={{ display: 'none' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {editFile ? (
                          <div style={{ display: 'flex', alignItems: 'center', flex: 1, padding: '6px 12px', borderRadius: '4px', border: '1px solid #D6E4FF', background: '#fff' }}>
                            <Icon icon="mdi:file-document" width={18} style={{ marginRight: '8px', color: '#1890FF' }} />
                            <Typography variant="body2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {editFile.name}
                            </Typography>
                            <IconButton size="small" onClick={() => setEditFile(null)} title="Remove file">
                              <Icon icon="mdi:close" width={18} color="#FF4842" />
                            </IconButton>
                          </div>
                        ) : editExistingUrl ? (
                          <div style={{ display: 'flex', alignItems: 'center', flex: 1, padding: '6px 12px', borderRadius: '4px', border: '1px solid #D6E4FF', background: '#fff' }}>
                            <Icon icon="mdi:file-document" width={18} style={{ marginRight: '8px', color: '#00AB55' }} />
                            <Typography variant="body2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {editExistingUrl.split('/').pop()}
                            </Typography>
                            <IconButton size="small" onClick={() => setEditExistingUrl(null)} title="Remove file">
                              <Icon icon="mdi:close" width={18} color="#FF4842" />
                            </IconButton>
                          </div>
                        ) : (
                          <CoreButton
                            variant="outlined"
                            size="small"
                            onClick={() => editFileRef.current.click()}
                            style={{ flex: 1, justifyContent: 'flex-start' }}
                          >
                            <Icon icon="mdi:file-upload" style={{ marginRight: '8px' }} />
                            Select new .srt or .vtt file
                          </CoreButton>
                        )}
                      </div>
                    </div>

                    {/* Edit language */}
                    <Select
                      value={editLanguage}
                      onChange={e => setEditLanguage(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      style={{ height: '36px' }}
                    >
                      {languages.map(lang => (
                        <MenuItem key={lang.countryCode} value={lang.countryCode}>
                          {lang.name} ({lang.countryCode})
                        </MenuItem>
                      ))}
                    </Select>

                    {/* Edit label */}
                    <TextField
                      label="Label"
                      value={editLabel}
                      onChange={e => setEditLabel(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />

                    {/* Edit default */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editIsDefault}
                          onChange={e => setEditIsDefault(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Default</Typography>}
                    />

                    {/* Edit actions */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <CoreButton
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleEditSave}
                        disabled={(!editFile && editIsDefault === editOriginalDefault) || editSaving}
                      >
                        {editSaving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                      </CoreButton>
                      <CoreButton
                        size="small"
                        variant="outlined"
                        onClick={cancelEdit}
                        disabled={editSaving}
                      >
                        Cancel
                      </CoreButton>
                    </div>
                  </div>
                </div>
              ) : (
                /* Normal row */
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: '#f5f5f5',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="mdi:subtitles" width={20} />
                    <Typography variant="body2">
                      {getLangLabel(sub.language)}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#637381' }}>
                      ({sub.language})
                    </Typography>
                    {sub.default && (
                      <span
                        style={{
                          fontSize: '11px',
                          background: '#00AB55',
                          color: '#fff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                        }}
                      >
                        Default
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <IconButton
                      size="small"
                      onClick={() => startEdit(sub)}
                      title="Edit"
                    >
                      <Icon icon="mdi:pencil" width={18} color="#637381" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeletingLang(sub.language)}
                      title="Delete"
                    >
                      <Icon icon="mdi:delete" width={18} color="#FF4842" />
                    </IconButton>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new subtitle */}
      {!showAddForm ? (
        <CoreButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setShowAddForm(true)}
          style={{ width: '100%' }}
        >
          <Icon icon="mdi:plus" style={{ marginRight: '6px' }} />
          Add Subtitle
        </CoreButton>
      ) : (
        <div
          style={{
            padding: '12px',
            borderRadius: '8px',
            background: '#F0F7FF',
            border: '1px solid #D6E4FF',
          }}
        >
          <Typography
            variant="subtitle2"
            style={{ marginBottom: '12px', fontWeight: 600 }}
          >
            Add Subtitle
          </Typography>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* File picker */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                accept=".srt,.vtt"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {selectedFile ? (
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderRadius: '4px', border: '1px solid #D6E4FF', background: '#fff' }}>
                  <Icon icon="mdi:file-document" width={18} style={{ marginRight: '8px', color: '#1890FF' }} />
                  <Typography variant="body2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedFile.name}
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedFile(null)} title="Remove file">
                    <Icon icon="mdi:close" width={18} color="#FF4842" />
                  </IconButton>
                </div>
              ) : (
                <CoreButton
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current.click()}
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                >
                  <Icon icon="mdi:file-upload" style={{ marginRight: '8px' }} />
                  Select .srt or .vtt file
                </CoreButton>
              )}
            </div>

            {/* Language selector */}
            <Select
              value={language}
              onChange={e => setLanguage(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              style={{ height: '40px' }}
            >
              {languages.map(lang => (
                <MenuItem key={lang.countryCode} value={lang.countryCode}>
                  {lang.name} ({lang.countryCode})
                </MenuItem>
              ))}
            </Select>

            {/* Label input */}
            <TextField
              label="Label (optional)"
              value={label}
              onChange={e => setLabel(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g. English, Deutsch"
            />

            {/* Default checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={e => setIsDefault(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2">Set as default subtitle</Typography>
              }
            />

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <CoreButton
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                color="primary"
                variant="contained"
              >
                {uploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Upload'
                )}
              </CoreButton>
              <CoreButton
                variant="outlined"
                onClick={() => {
                  setShowAddForm(false)
                  setSelectedFile(null)
                  setLabel('')
                  setIsDefault(false)
                }}
                disabled={uploading}
              >
                Cancel
              </CoreButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubtitleManager
